import React, { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { getAllProducts } from "../data/products";
import "./Products.css";
import { useCart } from "../context/CartContext";

const Products = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("featured");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const allProducts = getAllProducts();
  const { addToCart } = useCart();

  const getSortedProducts = (products) => {
    switch (sortBy) {
      case "price-low":
        return [...products].sort((a, b) => a.price - b.price);
      case "price-high":
        return [...products].sort((a, b) => b.price - a.price);
      case "name":
        return [...products].sort((a, b) => a.name.localeCompare(b.name));
      default:
        return products; // featured/default sorting
    }
  };

  const filteredProducts = getSortedProducts(
    allProducts.filter(
      (product) =>
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.description.toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  const fadeInUp = {
    initial: {
      opacity: 0,
      y: 30,
    },
    animate: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: [0.6, 0.05, 0.01, 0.99],
      },
    },
  };

  const fadeIn = {
    initial: {
      opacity: 0,
    },
    animate: {
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  const staggerChildren = {
    animate: {
      transition: {
        delayChildren: 0.4,
        staggerChildren: 0.1,
      },
    },
  };

  const handleSortChange = (value) => {
    setSortBy(value);
    setIsDropdownOpen(false);
  };

  return (
    <section className="products-section">
      <div className="products-hero">
        <div className="products-hero-content">
          <span className="products-label">Curated Collection</span>
          <h1>Luxury Aesthetic Essentials</h1>
          <div className="products-hero-divider"></div>
          <p className="products-hero-description">
            Discover curated luxury products and exclusive beauty essentials for
            your home. From premium skincare to elegant fragrances and décor,
            each item is thoughtfully selected to elevate your daily rituals.
          </p>
        </div>
      </div>

      <div className="products-content">
        <motion.div
          className="products-header"
          initial="initial"
          animate="animate"
          variants={fadeInUp}
        >
          <motion.div className="treatment-tags" variants={staggerChildren}>
            {[
              "Luxe Facials",
              "Luxe Massages",
              "Spa Packages",
              "Wellness Treatments",
            ].map((tag, index) => (
              <motion.span
                key={index}
                className="treatment-tag"
                variants={fadeIn}
              >
                <span className="tag-icon">◆</span>
                {tag}
              </motion.span>
            ))}
          </motion.div>

          <motion.div className="filters-container" variants={fadeIn}>
            <div className="search-wrapper">
              <input
                type="text"
                placeholder="Search products..."
                className="search-input"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <span className="search-icon">⌕</span>
            </div>

            <div className="custom-dropdown">
              <button
                className="dropdown-toggle"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              >
                Sort by:{" "}
                <span className="dropdown-selected">
                  {sortBy === "featured" && "Featured"}
                  {sortBy === "price-low" && "Price: Low to High"}
                  {sortBy === "price-high" && "Price: High to Low"}
                  {sortBy === "name" && "Name"}
                </span>
                <span className="dropdown-arrow">▼</span>
              </button>

              {isDropdownOpen && (
                <div className="dropdown-menu">
                  <button
                    className={`dropdown-item ${
                      sortBy === "featured" ? "active" : ""
                    }`}
                    onClick={() => handleSortChange("featured")}
                  >
                    Featured
                  </button>
                  <button
                    className={`dropdown-item ${
                      sortBy === "price-low" ? "active" : ""
                    }`}
                    onClick={() => handleSortChange("price-low")}
                  >
                    Price: Low to High
                  </button>
                  <button
                    className={`dropdown-item ${
                      sortBy === "price-high" ? "active" : ""
                    }`}
                    onClick={() => handleSortChange("price-high")}
                  >
                    Price: High to Low
                  </button>
                  <button
                    className={`dropdown-item ${
                      sortBy === "name" ? "active" : ""
                    }`}
                    onClick={() => handleSortChange("name")}
                  >
                    Name
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>

        <motion.div
          className="products-grid"
          variants={staggerChildren}
          initial="initial"
          animate="animate"
        >
          {filteredProducts.map((product) => (
            <motion.div
              key={product.id}
              className="product-card"
              variants={fadeInUp}
            >
              <Link
                to={`/product/${product.name
                  .toLowerCase()
                  .replace(/\s+/g, "-")}`}
                className="product-link"
              >
                <div className="product-image-container">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="product-image"
                  />
                  {product.tag && (
                    <motion.span className="product-tag" variants={fadeIn}>
                      {product.tag}
                    </motion.span>
                  )}
                  <motion.button
                    className="favorite-button"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    ♡
                  </motion.button>
                </div>
                <div className="product-info">
                  <h3 className="product-name">{product.name}</h3>
                  <p className="product-description">{product.description}</p>
                  <div className="product-details">
                    <span className="product-volume">{product.volume}</span>
                    <span className="product-price">${product.price}</span>
                  </div>
                </div>
              </Link>
              <motion.button
                className="add-to-cart"
                whileHover={{ opacity: 0.9 }}
                whileTap={{ scale: 0.98 }}
                onClick={(e) => {
                  e.preventDefault();
                  addToCart(product);
                }}
              >
                Add to Cart
              </motion.button>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Products;
